import React from 'react';
import { FaShare, FaTimes, FaTwitter, FaFacebook, FaWhatsapp, FaCopy } from 'react-icons/fa';
import type { RadioStation } from '../../types/radio';
import { useToast } from '../../contexts/ToastContext';
import { useTheme } from '../../contexts/ThemeContext';
import { createPortal } from 'react-dom';

interface ShareModalProps {
  station: RadioStation;
  onClose: () => void;
}

const ShareModal: React.FC<ShareModalProps> = ({ station, onClose }) => {
  const { showToast } = useToast();
  const { mode } = useTheme();
  const isFairyTheme = mode === 'fairy';
  const isNeonTheme = mode === 'neon';

  const shareUrl = `${window.location.origin}/station/${station.stationuuid}`;
  const shareText = `Listen to ${station.name} on AuraFY!`;

  const handleShare = async (platform?: string) => {
    try {
      // Ensure URL is properly encoded
      const encodedUrl = encodeURIComponent(shareUrl);
      const encodedText = encodeURIComponent(shareText);

      if (navigator.share && !platform) {
        await navigator.share({
          title: station.name,
          text: shareText,
          url: shareUrl,
        });
        showToast('Shared successfully!', 'success');
        onClose();
        return;
      }

      let shareLink = '';
      switch (platform) {
        case 'twitter':
          shareLink = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
          break;
        case 'facebook':
          shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
          break;
        case 'whatsapp':
          shareLink = `https://wa.me/?text=${encodedText} ${encodedUrl}`;
          break;
        case 'copy':
          await navigator.clipboard.writeText(shareUrl);
          showToast('Link copied to clipboard!', 'success');
          return;
      }

      if (shareLink) {
        window.open(shareLink, '_blank', 'width=600,height=400,noopener,noreferrer');
        onClose();
      }
    } catch (error) {
      console.error('Error sharing:', error);
      showToast('Failed to share. Please try again.', 'error');
    }
  };

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`${isFairyTheme
          ? 'bg-[#2a1f3f] border border-purple-500/20'
          : isNeonTheme
            ? 'bg-black/95 border border-[#00FF00]/20'
            : 'bg-gray-800'} 
        rounded-xl max-w-md w-full mx-4 p-6 relative animate-fade-in z-[10000]`}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className={`absolute top-4 right-4 ${isFairyTheme
            ? 'text-purple-200 hover:text-purple-100'
            : isNeonTheme
              ? 'text-[#00FF00]/80 hover:text-[#00FF00]'
              : 'text-gray-400 hover:text-white'} transition-colors`}
        >
          <FaTimes size={20} />
        </button>

        <div className="flex items-center space-x-2 mb-6">
          <FaShare size={24} className={isFairyTheme
            ? 'text-purple-400'
            : isNeonTheme
              ? 'text-[#00FF00]'
              : 'text-primary'} />
          <h3 className={`text-xl font-bold ${isFairyTheme
            ? 'text-purple-200'
            : isNeonTheme
              ? 'text-[#00FF00]'
              : 'text-white'}`}>
            Share Station
          </h3>
        </div>

        <div className="text-center mb-6">
          <h4 className={`text-lg font-semibold ${isFairyTheme
            ? 'text-purple-200'
            : isNeonTheme
              ? 'text-[#00FF00]'
              : 'text-white'} mb-2`}>
            {station.name}
          </h4>
          <p className={isFairyTheme
            ? 'text-purple-300'
            : isNeonTheme
              ? 'text-[#00FF00]/80'
              : 'text-gray-400'}>
            {station.country}
          </p>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-6">
          <button
            onClick={() => handleShare('twitter')}
            className={`flex flex-col items-center space-y-2 ${isFairyTheme
              ? 'text-purple-300 hover:text-[#1DA1F2]'
              : isNeonTheme
                ? 'text-[#00FF00]/70 hover:text-[#00FF00]'
                : 'text-gray-400 hover:text-[#1DA1F2]'} transition-colors`}
          >
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isFairyTheme
              ? 'bg-purple-500/20'
              : isNeonTheme
                ? 'bg-[#00FF00]/10'
                : 'bg-gray-700'}`}>
              <FaTwitter size={24} />
            </div>
            <span className="text-sm">Twitter</span>
          </button>

          <button
            onClick={() => handleShare('facebook')}
            className={`flex flex-col items-center space-y-2 ${isFairyTheme
              ? 'text-purple-300 hover:text-[#4267B2]'
              : isNeonTheme
                ? 'text-[#00FF00]/70 hover:text-[#00FF00]'
                : 'text-gray-400 hover:text-[#4267B2]'} transition-colors`}
          >
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isFairyTheme
              ? 'bg-purple-500/20'
              : isNeonTheme
                ? 'bg-[#00FF00]/10'
                : 'bg-gray-700'}`}>
              <FaFacebook size={24} />
            </div>
            <span className="text-sm">Facebook</span>
          </button>

          <button
            onClick={() => handleShare('whatsapp')}
            className={`flex flex-col items-center space-y-2 ${isFairyTheme
              ? 'text-purple-300 hover:text-[#25D366]'
              : isNeonTheme
                ? 'text-[#00FF00]/70 hover:text-[#00FF00]'
                : 'text-gray-400 hover:text-[#25D366]'} transition-colors`}
          >
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isFairyTheme
              ? 'bg-purple-500/20'
              : isNeonTheme
                ? 'bg-[#00FF00]/10'
                : 'bg-gray-700'}`}>
              <FaWhatsapp size={24} />
            </div>
            <span className="text-sm">WhatsApp</span>
          </button>

          <button
            onClick={() => handleShare('copy')}
            className={`flex flex-col items-center space-y-2 ${isFairyTheme
              ? 'text-purple-300 hover:text-purple-100'
              : isNeonTheme
                ? 'text-[#00FF00]/70 hover:text-[#00FF00]'
                : 'text-gray-400 hover:text-white'} transition-colors`}
          >
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isFairyTheme
              ? 'bg-purple-500/20'
              : isNeonTheme
                ? 'bg-[#00FF00]/10'
                : 'bg-gray-700'}`}>
              <FaCopy size={24} />
            </div>
            <span className="text-sm">Copy Link</span>
          </button>
        </div>

        {typeof navigator !== 'undefined' && 'share' in navigator && (
          <button
            onClick={() => handleShare()}
            className={`w-full py-3 rounded-lg transition-colors ${isFairyTheme
              ? 'bg-purple-500 hover:bg-purple-600 text-white'
              : isNeonTheme
                ? 'bg-[#00FF00] hover:bg-[#00FF00]/90 text-black'
                : 'bg-primary hover:bg-primary/90 text-white'}`}
          >
            Share using device
          </button>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ShareModal;